// Invoice interfaces matching backend structure
export enum EInvoiceType {
  JOB = "job",
  QUOTE = "quote",
  SETUP_FEE = "setup_fee",
  SERVICE_USAGE = "service_usage",
}

export enum EQuantityUnit {
  MINUTES = "minutes",
  HOURS = "hours",
  DAYS = "days",
  WEEKS = "weeks",
  MONTHS = "months",
  YEARS = "years",
  PER_ITEM = "per_item",
  PER_SQUARE_FOOT = "per_square_foot",
}

export enum EMonetaryAmountBy {
  MONTHLY = "monthly",
  YEARLY = "yearly",
  UNIQUE = "unique",
  PER_ITEM = "per_item",
  PER_SQUARE_FOOT = "per_square_foot",
  PER_CAR_SPACE = "per_car_space",
  PER_FLOOR = "per_floor",
}

export enum ECurrency {
  USD = "USD",
  MXN = "MXN",
}

export interface IQuantityAmount {
  value: number;
  unit: EQuantityUnit;
}

export interface IMonetaryAmount {
  value: number;
  by: EMonetaryAmountBy;
  currency: ECurrency;
}

export interface IInvoiceRecord {
  quantity: IQuantityAmount;
  price: IMonetaryAmount;
  total: IMonetaryAmount;
  alias?: string;
}

export enum EPaymentMethod {
  STRIPE = "stripe",
  PAYPAL = "paypal",
  BANK_TRANSFER = "bank_transfer",
  MANUAL_ENTRY = "manual_entry",
}

// Interfaces for creating invoices (without transaction fields - these are set by webhooks)
export interface IPaymentBankTransferCreate {
  provider: EPaymentMethod.BANK_TRANSFER;
  bankTransferInfo: {
    routingNumber: string;
    accountHolderName: string;
    accountNumber?: string;
    address?: string;
  };
}

export interface IPaymentOnlineCreate {
  provider: EPaymentMethod.STRIPE | EPaymentMethod.PAYPAL;
  isPlatform?: boolean;
}

export interface IPaymentManualEntryCreate {
  provider: EPaymentMethod.MANUAL_ENTRY;
  notes?: string; // Optional notes about the manual payment
  transactionId?: string; // Optional transaction ID or reference number
}

export type TPaymentMethodCreate = IPaymentBankTransferCreate | IPaymentOnlineCreate | IPaymentManualEntryCreate;

// Interfaces for complete payment methods (with transaction fields populated by webhooks)
export interface IPaymentBankTransfer extends IPaymentBankTransferCreate {
  transactionId?: string | null;
  paidAt?: string | Date | null;
  fee?: number | null;
  netAmount?: number | null;
  grossAmount?: number | null;
  webhookId?: string | null;
}

export interface IPaymentOnline extends IPaymentOnlineCreate {
  externalOrderUid?: string | null;
  externalMerchantId?: string | null;
  paymentUrl?: string | null;
  transactionId?: string | null;
  paidAt?: string | Date | null;
  fee?: number | null;
  netAmount?: number | null;
  grossAmount?: number | null;
  webhookId?: string | null;
}

export interface IPaymentManualEntry extends IPaymentManualEntryCreate {
  transactionId?: string | null;
  paidAt?: string | Date | null;
  fee?: number | null;
  netAmount?: number | null;
  grossAmount?: number | null;
  webhookId?: string | null;
}

export type TPaymentMethod = IPaymentBankTransfer | IPaymentOnline | IPaymentManualEntry;

export enum EEmailPermissionLevel {
  CAN_PAY = "can_pay",
  CAN_VIEW = "can_view",
}

export interface IEmailRecipient {
  email: string;
  permissionLevel: EEmailPermissionLevel;
  isLocked?: boolean;
}

/** Optional snapshot from create-invoice UI (qty / unit price / overrides). Matches backend JSONB. */
export interface IInvoicePricingDetail {
  serviceLine: {
    qty: number;
    unitPrice: number;
    lineTotal: number;
    isLineTotalOverridden: boolean;
  };
  isGlobalTotalOverridden: boolean;
  computedGlobalTotal?: number;
  externalUid?: string;
  companyId?: number;
  phoneNumber?: string;
  serviceFeeLabel?: string;
  extraLineItems?: Array<{
    id?: string;
    title: string;
    amount: number;
    unitPrice?: number;
    qty?: number;
    description?: string;
    kind?: "amount" | "percentage";
    value?: number;
    allocations?: Array<{
      eventUid: string;
      amount: number;
      unitPrice?: number;
    }>;
    appliesTo?: {
      type: "invoice" | "service" | "extra_line_item";
      jobId?: number | null;
      eventUid?: string | null;
      extraLineItemId?: string | null;
      label?: string | null;
    };
  }>;
  serviceFees?: Array<{
    alias?: string | null;
    kind?: "amount" | "percentage";
    value?: number;
    quantity: { value: number; unit: string };
    price: { value: number; by: string; currency: string };
    total: { value: number; by: string; currency: string };
    appliesTo?: {
      type: "invoice" | "service" | "extra_line_item";
      jobId?: number | null;
      eventUid?: string | null;
      extraLineItemId?: string | null;
      label?: string | null;
    };
  }>;
  discounts?: Array<{
    description: string;
    kind: "amount" | "percentage";
    value: number;
    amount: number;
    appliesTo?: {
      type: "invoice" | "service" | "extra_line_item";
      jobId?: number | null;
      eventUid?: string | null;
      extraLineItemId?: string | null;
      label?: string | null;
    };
  }>;
  serviceLines?: Array<{
    jobId: number;
    qty: number;
    unitPrice: number;
    lineTotal: number;
    isLineTotalOverridden: boolean;
    eventUid?: string;
  }>;
  minimumPaymentAmount?: number;
  tip?: {
    kind: "amount" | "percentage";
    value: number;
    amount: number;
    baseTotal: number;
  } | null;
}

export interface ICreateInvoicePayload {
  scopeOfWorkUid?: string;

  companyName?: string;

  jobEventUid?: string;

  billingJobEvents?: { uid: string }[];

  identifierId?: string;

  pricingDetail?: IInvoicePricingDetail | null;

  mainAreasCost?: IInvoiceRecord;

  otherAreasCost?: IInvoiceRecord;

  additionalObjectsCost?: IInvoiceRecord;

  specialRequestsCost?: IInvoiceRecord;

  increasePercentageByFrequency?: IInvoiceRecord;

  totalCost: IInvoiceRecord;

  paymentMethods: TPaymentMethodCreate[];

  emailRecipients?: IEmailRecipient[];

  notes?: string | null;

  sendEmail?: boolean;

  allowPartialPayments?: boolean;

  parentDepositInvoiceUid?: string | null;

  creditedDepositAmount?: number | null;
}

export interface IInvoiceBillingJobEvent {
  uid: string;
  jobId: number;
  status: string;
  startDate: string;
  endDate: string;
  job?: {
    id: number;
    clientUid: string;
    /** Present when job has a sector / SOW document URL */
    scopeOfWorkUrl?: string | null;
    alias?: string | null;
    client?: {
      uid: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    company?: {
      id: number;
      businessName: string;
      email: string;
      address?: string;
    };
    scopeOfWorkUid?: string;
  };
}

// Invoice response interface (matches backend IInvoice)
export interface IInvoice {
  uid: string;
  type?: EInvoiceType;
  scopeOfWorkUid: string;
  /** Legacy: first billed event UID (populated by backend `attachLegacyJobEventFields`). */
  jobEventUid?: string;
  identifierId?: string;
  mainAreasCost: IInvoiceRecord;
  otherAreasCost: IInvoiceRecord;
  additionalObjectsCost: IInvoiceRecord;
  specialRequestsCost: IInvoiceRecord;
  increasePercentageByFrequency: IInvoiceRecord;
  totalCost: IInvoiceRecord;
  tipAmount?: number | null;
  paymentMethods: TPaymentMethod[];
  paidWithPaymentMethod?: EPaymentMethod | null;
  paidAt?: string | null;
  refundedAt?: string | null;
  refundedWithPaymentMethod?: string | null;
  cancelledAt?: string | null;
  emailRecipients?: IEmailRecipient[];
  notes?: string | null;
  parentDepositInvoiceUid?: string | null;
  creditedDepositAmount?: number | null;
  pricingDetail?: IInvoicePricingDetail | null;
  lastResendAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  allowPartialPayments?: boolean;
  /** All billed job events (multi-visit). Backend always includes via `billingJobEvents` association. */
  billingJobEvents?: IInvoiceBillingJobEvent[];
  /** Legacy: first billed event (populated by backend `attachLegacyJobEventFields`). */
  jobEvent?: IInvoiceBillingJobEvent;
  /** Magic-link viewer may pay this invoice (from token canPay claim). */
  viewerCanPay?: boolean;
  payments?: IInvoicePayment[];
  /** Associated quote (populated when invoice is linked to a deposit invoice). */
  quote?: IQuote;
  /** Deposit invoice this final invoice credits against (populated when included). */
  parentDepositInvoice?: Pick<IInvoice, "uid" | "totalCost" | "paidAt">;
}

export interface IInvoicePayment {
  uid?: string;
  invoiceUid?: string;
  amount: number | string;
  status: string;
  paidAt?: string | null;
  provider?: string | null;
  paymentMethod?: string | null;
  transactionId?: string | null;
  gatewayReference?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface IAvailablePaymens {
  paypal: boolean;
  stripe: boolean;
}

export interface IRefundInvoicePayload {
  amount?: number;
  reason?: string;
}

export interface IRefundInvoiceResponse {
  status: number;
  message: string;
  data?: {
    refundedAt: string;
    refundedWithPaymentMethod: EPaymentMethod | string;
  };
}

// Quote-related interfaces
export interface IQuoteScopeOfWork {
  name: string;
  clientUid: string;
  companyId: number;
  averagePeople: number;
  frequencyType: string;
  squareFootage: string;
  typeOfService?: {
    costBias: number;
    baseRatePerHour: number;
  };
  coverageAreaId: number;
  typeOfServiceUid: string;
}

export interface IQuoteService {
  id: string;
  alias: string;
  notes?: string;
  price: string | number;
  total: number;
  units: string | number;
  measure: string;
  service: string;
  companyId: number;
  serviceUid: string;
  scopeOfWork?: IQuoteScopeOfWork;
}

export interface IQuote {
  uid?: string;
  id?: string | number;
  services?: IQuoteService[];
  /** Configured deposit amount on the quote (when a deposit invoice exists). */
  depositAmount?: number | null;
  depositInvoiceUid?: string;
  depositInvoice?: {
    uid: string;
    paidAt?: string | null;
    cancelledAt?: string | null;
    refundedAt?: string | null;
  };
  total?: number;
  currency?: string;
  companyId?: number;
  clientUid?: string;
  client?: {
    uid: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    clientCompanyName?: string;
  };
  company?: {
    id: number;
    businessName: string;
    email: string;
    address?: string;
  };
}
